<script setup lang="ts">
import { computed, ref } from "vue";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";
import { format as formatDate } from "date-fns";
import { BookmarkPlus, Loader2 } from "@lucide/vue";

import { useModalStore } from "@/stores/modals/modals.store";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import { useProjectRevisionsStore } from "../stores/projectRevisions.store";

// Save-a-revision dialog. One request: the backend freezes the revision being
// superseded, moves the working rows onto the new revision (copy-on-write), pins the Marten stream
// version for materials, and makes the new revision current. Admins only (the caller gates the
// entry button; the endpoint enforces it too).
const props = defineProps<{
  projectId: string;
  projectName: string;
}>();

const toast = useToast();
const { closeByName } = useModalStore();
const revisionsStore = useProjectRevisionsStore();

// Down to the minute, not the day: two save points taken on the same day would otherwise share a
// name, and the banner and switcher name revisions rather than number them — "Viewing revision X …
// changes are saved to the current revision X" is not a sentence anyone can act on.
const revisionName = ref(`${props.projectName} — ${formatDate(new Date(), "MMM d, HH:mm")}`);
const isSaving = ref(false);

const nameError = computed(() => (revisionName.value.trim() ? "" : "Give the revision a name"));

const cancel = () => closeByName("SaveProjectRevisionModal", { action: "cancel" });

const save = async () => {
  if (nameError.value || isSaving.value) return;
  isSaving.value = true;
  try {
    const revisionId = await revisionsStore.saveRevision(props.projectId, revisionName.value.trim());
    toast.success(`Revision “${revisionName.value.trim()}” saved and set as current.`);
    closeByName("SaveProjectRevisionModal", { action: "saved", revisionId });
  } catch (e) {
    Sentry.captureException(e);
    toast.error("Could not save the revision. Please try again.");
  } finally {
    isSaving.value = false;
  }
};

const btnBase =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";
</script>

<template>
  <div class="flex flex-col gap-4">
    <h2 class="t-sh4 text-type-default">Save current state as a revision</h2>

    <p class="t-small text-type-muted">
      A revision is a named save point of the whole project — everything as it is right now. Revisions are kept permanently and
      the new revision becomes the
      <span class="t-small-bold text-type-default">current</span> one for everyone.
    </p>

    <TextInputNew
      v-model="revisionName"
      label="Revision name"
      :error-msg="nameError"
      :disabled="isSaving"
      required />

    <div class="gap-px-8 flex justify-end">
      <button
        type="button"
        :disabled="isSaving"
        :class="[
          btnBase,
          'bg-slate-0 text-type-default border border-slate-200 hover:bg-slate-100',
          { 'interactive-disabled': isSaving },
        ]"
        data-cy="save-revision-cancel"
        @click="cancel">
        Cancel
      </button>
      <button
        type="button"
        :disabled="!!nameError || isSaving"
        :class="[
          btnBase,
          !nameError && !isSaving
            ? 'bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover text-white'
            : 'text-type-muted bg-slate-200',
          { 'interactive-disabled': !!nameError || isSaving },
        ]"
        data-cy="save-revision-confirm"
        @click="save">
        <Loader2
          v-if="isSaving"
          class="icon-sm animate-spin"
          :stroke-width="1.5" />
        <BookmarkPlus
          v-else
          class="icon-sm"
          :stroke-width="1.5" />
        {{ isSaving ? "Saving…" : "Save revision" }}
      </button>
    </div>
  </div>
</template>
